import React, { useEffect, useState } from "react";
import { Download, Share, Check, Tv2 } from "lucide-react";
import { toast } from "sonner";
import { ON_DEMAND_MANIFEST, loadedManifest, isStandaloneDisplay } from "../lib/manifest-switch";

const isIOS = () => typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "");

const OnDemandInstallCta = () => {
  const [installed, setInstalled] = useState(false);
  const [canPrompt, setCanPrompt] = useState(false);

  useEffect(() => {
    setInstalled(isStandaloneDisplay());
    setCanPrompt(Boolean(window.__gfnDeferredInstall));
    const onAvail = () => setCanPrompt(true);
    const onDone = () => {
      setCanPrompt(false);
      setInstalled(true);
      toast.success("GFN Soul On Demand installed");
    };
    window.addEventListener("gfn-install-available", onAvail);
    window.addEventListener("gfn-install-done", onDone);
    return () => {
      window.removeEventListener("gfn-install-available", onAvail);
      window.removeEventListener("gfn-install-done", onDone);
    };
  }, []);

  const install = async () => {
    if (isIOS()) {
      toast("Tap Share, then “Add to Home Screen”", { icon: <Share className="w-4 h-4 text-gold" /> });
      return;
    }
    // Chrome only installs the manifest it read at page load. If this page was
    // reached via in-app navigation, reload it so the On Demand manifest is active.
    if (loadedManifest() !== ON_DEMAND_MANIFEST) {
      window.location.assign("/on-demand?install=1");
      return;
    }
    const evt = window.__gfnDeferredInstall;
    if (!evt) {
      toast("Use your browser menu → “Install app” to add GFN Soul On Demand");
      return;
    }
    evt.prompt();
    try {
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") setInstalled(true);
    } catch (_) {
      /* ignore */
    }
    window.__gfnDeferredInstall = null;
    setCanPrompt(false);
  };

  return (
    <div
      data-testid="ondemand-install-card"
      className="h-full rounded-lg border border-gold/30 bg-jet-surface/80 backdrop-blur p-5 md:p-6 flex flex-col gap-4 relative overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold/10 blur-3xl rounded-full" aria-hidden />
      <div className="flex items-center gap-3">
        <img
          src="/icon-ondemand-192.png"
          alt="GFN Soul On Demand app icon"
          className="w-14 h-14 rounded-2xl shadow-[0_0_30px_rgba(212,175,55,0.35)]"
          data-testid="ondemand-app-icon"
        />
        <div>
          <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">Web App</div>
          <div className="font-display text-2xl text-white uppercase leading-none">GFN Soul On Demand</div>
        </div>
      </div>
      <p className="font-body text-sm text-neutral-300">
        Download the web app to watch <span className="text-gold">Live TV</span> and the full on-demand
        catalog full-screen from your home screen. Free · no app store.
      </p>
      <ul className="grid grid-cols-2 gap-2 text-[11px] font-condensed uppercase tracking-widest text-neutral-400">
        <li className="flex items-center gap-1.5"><Tv2 className="w-3.5 h-3.5 text-gold" /> Live channel</li>
        <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-gold" /> One-tap launch</li>
        <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-gold" /> Full-screen</li>
        <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-gold" /> Radio keeps playing</li>
      </ul>
      <div className="mt-auto">
        {installed ? (
          <div
            data-testid="ondemand-install-done"
            className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-5 py-3 bg-gold/10 text-gold font-condensed uppercase tracking-widest text-xs"
          >
            <Check className="w-4 h-4" /> Installed on this device
          </div>
        ) : (
          <button
            data-testid="ondemand-install-btn"
            onClick={install}
            className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-6 py-3.5 rounded-full transition-colors shadow-[0_0_30px_rgba(212,175,55,0.25)]"
          >
            <Download className="w-4 h-4" />
            {canPrompt ? "Download Web App · Watch Live TV" : "Download Web App"}
          </button>
        )}
      </div>
    </div>
  );
};

export default OnDemandInstallCta;
